import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material'
import ReactJson from '@microlink/react-json-view'
import { usePatientFilter } from '../hooks/usePatientFilter'
import { ptmAPI } from '../services/api'

function PtmJsonViewer({ src }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={0.5}>
        <Button
          size="small"
          onClick={() => setExpanded(prev => !prev)}
          sx={{ textTransform: 'none', fontSize: '0.7rem', minWidth: 0, py: 0 }}
        >
          {expanded ? 'Collapse All' : 'Expand All'}
        </Button>
      </Box>
      <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
        <ReactJson
          src={src}
          name={false}
          collapsed={expanded ? false : 2}
          displayDataTypes={false}
          displayObjectSize={true}
          enableClipboard={true}
          theme="rjv-default"
          style={{ fontSize: '0.8rem', padding: '8px', borderRadius: '4px', backgroundColor: '#f9fafb' }}
        />
      </Box>
    </Box>
  )
}

export default function TherapeuticModels() {
  const { selectedPatient, hasSelectedPatient } = usePatientFilter()
  const [ptmData, setPtmData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!hasSelectedPatient()) {
      setPtmData(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)
    ptmAPI.get(selectedPatient.id)
      .then(data => setPtmData(data))
      .catch(() => {
        setPtmData(null)
        setError('No therapeutic model found for this patient.')
      })
      .finally(() => setLoading(false))
  }, [selectedPatient])

  const ptmFields = ptmData ? [
    { label: 'Compiled Briefing', value: ptmData.compiled_briefing, type: 'text' },
    { label: 'Session Strategy', value: ptmData.session_strategy, type: 'text' },
    { label: 'Treatment Arc Summary', value: ptmData.treatment_arc_summary, type: 'text' },
    { label: 'Issue Register', value: ptmData.issue_register, type: 'json' },
    { label: 'Disposition Profile', value: ptmData.disposition_profile, type: 'json' },
    { label: 'Therapeutic Hypotheses', value: ptmData.therapeutic_hypotheses, type: 'json' },
    { label: 'Alliance Profile', value: ptmData.alliance_profile, type: 'json' },
  ].filter(f => f.value && (typeof f.value === 'string' ? f.value.trim() : (Array.isArray(f.value) ? f.value.length > 0 : Object.keys(f.value).length > 0))) : []

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Therapeutic Models</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View patient therapeutic model data including issue registers, disposition profiles, and therapeutic hypotheses.
      </Typography>

      {!hasSelectedPatient() ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <PsychologyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            Select a patient from the dropdown above to view their therapeutic model.
          </Typography>
        </Paper>
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{error}</Typography>
        </Paper>
      ) : ptmFields.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Therapeutic model exists but all fields are empty.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <PsychologyIcon color="primary" />
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                {selectedPatient.name || selectedPatient.email || 'Unnamed'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                v{ptmData.version || 0} · {ptmData.session_count || 0} sessions
                {ptmData.updated_at && ` · Updated ${new Date(ptmData.updated_at).toLocaleDateString()}`}
              </Typography>
            </Box>
          </Box>

          {ptmFields.map((field, idx) => (
            <Accordion key={field.label} defaultExpanded={idx === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">{field.label}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {field.type === 'json' ? (
                  <PtmJsonViewer src={field.value} />
                ) : (
                  <Box
                    component="pre"
                    sx={{
                      bgcolor: 'grey.50',
                      p: 2,
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 500,
                      fontSize: '0.8rem',
                      m: 0,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {field.value}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}
    </Box>
  )
}
